import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Save, Wrench, CalendarCheck } from "lucide-react";
import { toast } from "sonner";

const dayDiff = (d) => Math.round((new Date(`${d}T00:00:00`) - new Date(new Date().toDateString())) / 86400000);

export function DueBadge({ date }) {
  if (!date) return <Badge variant="outline" className="text-[10px]">sans date</Badge>;
  const n = dayDiff(date);
  if (n < 0) return <Badge className="bg-red-600 text-[10px]" data-testid="due-expired">échu depuis {-n} j</Badge>;
  if (n <= 30) return <Badge className="bg-orange-500 text-[10px]" data-testid="due-soon">dans {n} j</Badge>;
  return <Badge className="bg-emerald-600 text-[10px]" data-testid="due-ok">dans {n} j</Badge>;
}

export function busAlertLevel(bus) {
  const days = (bus.compliance || []).filter((c) => c.due_date).map((c) => dayDiff(c.due_date));
  if (!days.length) return null;
  const min = Math.min(...days);
  return min < 0 ? "expired" : min <= 30 ? "soon" : "ok";
}

function Field({ label, className = "", ...p }) {
  return <div className={className}><Label className="text-xs">{label}</Label><Input className="mt-1 h-9" {...p} /></div>;
}

export default function BusSheet({ bus, garages, onSaved }) {
  const [form, setForm] = useState(bus);
  const [comp, setComp] = useState(bus.compliance || []);
  const [logs, setLogs] = useState([]);
  const [entry, setEntry] = useState({ date: new Date().toISOString().slice(0, 10), kind: "Entretien", description: "", vendor: "", cost: "", odometer: "" });

  const loadLogs = () => api.get(`/buses/${bus.id}/maintenance`).then((r) => setLogs(r.data)).catch(() => {});
  useEffect(() => { setForm(bus); setComp(bus.compliance || []); loadLogs(); }, [bus.id]); // eslint-disable-line

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    try {
      const body = {
        fleet_number: form.fleet_number, plate: form.plate || "", type: form.type || "",
        status: form.status || "active", make: form.make || "", model: form.model || "",
        notes: form.notes || "", gps_device: form.gps_device || "",
        capacity: form.capacity ? +form.capacity : undefined,
        year: form.year ? +form.year : undefined,
        vin: form.vin || "", odometer: form.odometer ? +form.odometer : undefined,
        garage_id: form.garage_id,
        compliance: comp.filter((c) => c.label).map((c) => ({ id: c.id, label: c.label, due_date: c.due_date || "", notes: c.notes || "" })),
      };
      const { data } = await api.put(`/buses/${bus.id}`, body);
      toast.success("Fiche enregistrée");
      onSaved?.(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const addLog = async () => {
    if (!entry.date) return toast.error("Date requise");
    try {
      await api.post(`/buses/${bus.id}/maintenance`, {
        ...entry, cost: entry.cost ? +entry.cost : null,
        odometer: entry.odometer ? +entry.odometer : null,
      });
      toast.success("Entretien ajouté");
      setEntry({ ...entry, description: "", vendor: "", cost: "", odometer: "" });
      loadLogs();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="bg-secondary/40 border-t border-border px-5 py-5 space-y-6" data-testid="bus-sheet">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Identité du véhicule</div>
        <div className="grid md:grid-cols-4 gap-3">
          <Field label="N° flotte" value={form.fleet_number || ""} onChange={set("fleet_number")} data-testid="sheet-fleet" />
          <Field label="Plaque" value={form.plate || ""} onChange={set("plate")} data-testid="sheet-plate" />
          <Field label="Marque" value={form.make || ""} onChange={set("make")} data-testid="sheet-make" />
          <Field label="Modèle" value={form.model || ""} onChange={set("model")} />
          <Field label="Année" type="number" value={form.year || ""} onChange={set("year")} />
          <Field label="NIV / VIN" value={form.vin || ""} onChange={set("vin")} />
          <Field label="Odomètre (km)" type="number" value={form.odometer || ""} onChange={set("odometer")} data-testid="sheet-odometer" />
          <Field label="Capacité" type="number" value={form.capacity || ""} onChange={set("capacity")} />
          <Field label="Type" value={form.type || ""} onChange={set("type")} />
          <Field label="Statut" value={form.status || ""} onChange={set("status")} />
          <Field label="GPS" value={form.gps_device || ""} onChange={set("gps_device")} />
          <div>
            <Label className="text-xs">Garage</Label>
            <select className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              value={form.garage_id || ""} onChange={set("garage_id")} data-testid="sheet-garage">
              {garages.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
          <Field className="md:col-span-4" label="Notes" value={form.notes || ""} onChange={set("notes")} />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
            <CalendarCheck className="w-4 h-4" /> Dates importantes / conformité
          </div>
          <Button size="sm" variant="outline" data-testid="add-compliance"
            onClick={() => setComp([...comp, { label: "", due_date: "", notes: "" }])}>
            <Plus className="w-4 h-4 mr-1" /> Ajouter une date
          </Button>
        </div>
        {!comp.length && <div className="text-sm text-muted-foreground">Aucune date suivie. Ajoutez celles qui vous concernent (vérification mécanique, immatriculation, extincteur, etc.).</div>}
        <div className="space-y-2">
          {comp.map((c, i) => (
            <div key={i} className="grid md:grid-cols-12 gap-3 items-end" data-testid={`compliance-row-${i}`}>
              <Field className="md:col-span-4" label="Élément suivi" value={c.label} data-testid={`compliance-label-${i}`}
                onChange={(e) => setComp(comp.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
              <Field className="md:col-span-3" label="Échéance" type="date" value={c.due_date || ""} data-testid={`compliance-date-${i}`}
                onChange={(e) => setComp(comp.map((x, j) => (j === i ? { ...x, due_date: e.target.value } : x)))} />
              <Field className="md:col-span-3" label="Note" value={c.notes || ""}
                onChange={(e) => setComp(comp.map((x, j) => (j === i ? { ...x, notes: e.target.value } : x)))} />
              <div className="md:col-span-2 flex items-center gap-2 pb-1">
                <DueBadge date={c.due_date} />
                <Button size="sm" variant="ghost" data-testid={`compliance-del-${i}`}
                  onClick={() => setComp(comp.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Button onClick={save} data-testid="save-bus-sheet"><Save className="w-4 h-4 mr-1" /> Enregistrer la fiche</Button>

      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3 flex items-center gap-2">
          <Wrench className="w-4 h-4" /> Historique d'entretien et inspections
        </div>
        <div className="grid md:grid-cols-7 gap-3 items-end mb-3">
          <Field label="Date" type="date" value={entry.date} onChange={(e) => setEntry({ ...entry, date: e.target.value })} data-testid="mt-date" />
          <div><Label className="text-xs">Type</Label>
            <select className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              value={entry.kind} onChange={(e) => setEntry({ ...entry, kind: e.target.value })} data-testid="mt-kind">
              {["Entretien", "Inspection", "Réparation", "Pneus", "Huile", "Ronde de sécurité", "Autre"].map((k) => <option key={k} value={k}>{k}</option>)}
            </select></div>
          <Field className="md:col-span-2" label="Description" value={entry.description} onChange={(e) => setEntry({ ...entry, description: e.target.value })} data-testid="mt-desc" />
          <Field label="Fournisseur" value={entry.vendor} onChange={(e) => setEntry({ ...entry, vendor: e.target.value })} />
          <Field label="Coût ($)" type="number" value={entry.cost} onChange={(e) => setEntry({ ...entry, cost: e.target.value })} />
          <Field label="Odomètre" type="number" value={entry.odometer} onChange={(e) => setEntry({ ...entry, odometer: e.target.value })} />
          <Button className="md:col-span-7" onClick={addLog} data-testid="add-maintenance"><Plus className="w-4 h-4 mr-1" /> Ajouter à l'historique</Button>
        </div>
        <div className="border border-border rounded-lg divide-y divide-border overflow-hidden bg-card">
          {!logs.length && <div className="text-sm text-muted-foreground py-6 text-center">Aucun entretien enregistré.</div>}
          {logs.map((m, i) => (
            <div key={m.id} className="grid grid-cols-12 gap-3 px-4 py-2.5 text-sm items-center" data-testid={`maintenance-row-${i}`}>
              <div className="col-span-2 font-mono">{m.date}</div>
              <div className="col-span-2"><Badge variant="secondary">{m.kind}</Badge></div>
              <div className="col-span-4 truncate">{m.description || "—"}</div>
              <div className="col-span-2 text-muted-foreground truncate">{m.vendor || "—"}</div>
              <div className="col-span-1 font-mono">{m.cost ? `${m.cost} $` : "—"}</div>
              <div className="col-span-1 text-right">
                <Button size="sm" variant="ghost" data-testid={`maintenance-del-${i}`}
                  onClick={async () => { await api.delete(`/buses/${bus.id}/maintenance/${m.id}`); loadLogs(); }}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
